import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { Waves } from "@/components/Footer";

const MaskedLine = ({ text, delay, className }) => (
  <span className="block overflow-hidden">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {text}
    </motion.span>
  </span>
);

const CODE_SNIPPET = `function solve(problem) {
  let solution = analyze(problem);
  while (!solution.isOptimal()) {
    solution = refine(solution);
  }
  return solution;
}`;

const D20Wire = ({ className }) => (
  <svg viewBox="0 0 200 200" className={className} fill="none" stroke="currentColor" strokeWidth="0.8" aria-hidden="true">
    <polygon points="100,10 178,55 178,145 100,190 22,145 22,55" />
    <polygon points="100,10 178,145 22,145" />
    <polygon points="178,55 100,190 22,55" />
  </svg>
);

const Hero = () => {
  const { t } = useLanguage();
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 55, damping: 18 });
  const sy = useSpring(my, { stiffness: 55, damping: 18 });
  const x1 = useTransform(sx, (v) => v * 26);
  const y1 = useTransform(sy, (v) => v * 26);

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section onMouseMove={onMove} className="relative flex min-h-screen items-center overflow-hidden px-6 py-28 md:px-12" data-testid="hero-section">
      <Waves />
      <pre aria-hidden="true" className="pointer-events-none absolute right-10 top-28 hidden select-none font-mono text-[11px] leading-relaxed text-white/[0.07] lg:block">
        {CODE_SNIPPET}
      </pre>
      <div className="pointer-events-none absolute left-1/2 top-1/2 h-[36rem] w-[36rem] -translate-x-1/2 -translate-y-1/2 rounded-full bg-neon/[0.06] blur-3xl" />

      <div className="relative mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-14 md:grid-cols-12">
        <motion.div style={{ x: x1, y: y1 }} className="relative md:col-span-5">
          <div className="pointer-events-none absolute left-1/2 top-1/2 w-[135%] -translate-x-1/2 -translate-y-1/2">
            <D20Wire className="animate-spin-slow w-full text-neon/15" />
          </div>
          <div className="pointer-events-none absolute left-1/2 top-1/2 w-[170%] -translate-x-1/2 -translate-y-1/2">
            <D20Wire className="animate-spin-slower-reverse w-full text-white/[0.05]" />
          </div>
          <motion.img
            src="/assets/fc-logo.webp"
            alt="Logo FC — Fabio Comelli"
            data-testid="hero-logo"
            className="relative mx-auto w-52 md:w-72 lg:w-80"
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1, y: [0, -12, 0] }}
            transition={{
              opacity: { duration: 1, delay: 0.1 },
              scale: { duration: 1, delay: 0.1, ease: [0.16, 1, 0.3, 1] },
              y: { duration: 6, repeat: Infinity, ease: "easeInOut" },
            }}
          />
        </motion.div>

        <div className="text-center md:col-span-7 md:text-left">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.2 }}
            className="mb-6 font-mono text-xs uppercase tracking-[0.3em] text-muted2"
            data-testid="hero-kicker"
          >
            {t.hero.kicker}
          </motion.p>

          <h1 className="font-display font-extrabold uppercase" data-testid="hero-title">
            <MaskedLine text={t.hero.name} delay={0.25} className="text-4xl tracking-[0.06em] sm:text-6xl lg:text-7xl xl:text-8xl" />
          </h1>

          <motion.div
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 0.9, delay: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="mx-auto mt-7 h-px w-56 origin-left bg-neon/70 md:mx-0"
          />

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="mt-7 font-mono text-sm uppercase tracking-[0.45em] text-neon md:text-base"
            data-testid="hero-role"
          >
            {t.hero.role}
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="mt-3 font-mono text-xs uppercase tracking-[0.3em] text-muted2"
            data-testid="hero-motto"
          >
            {t.hero.motto}
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.0, ease: [0.16, 1, 0.3, 1] }}
            className="mx-auto mt-8 max-w-md text-base leading-relaxed text-muted2 md:mx-0 md:text-lg"
            data-testid="hero-subtitle"
          >
            {t.hero.sub}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.15, ease: [0.16, 1, 0.3, 1] }}
            className="mt-10 flex flex-wrap items-center justify-center gap-4 md:justify-start"
          >
            <a
              href="#apps"
              data-testid="hero-cta-apps"
              className="group inline-flex items-center gap-2 rounded-full bg-neon px-7 py-3.5 font-mono text-xs font-bold uppercase tracking-widest text-black transition-transform duration-300 hover:scale-105"
            >
              {t.hero.ctaApps}
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
            <a
              href="#contact"
              data-testid="hero-cta-contact"
              className="inline-flex items-center gap-2 rounded-full border border-white/20 px-7 py-3.5 font-mono text-xs uppercase tracking-widest text-white transition-colors duration-300 hover:border-neon hover:text-neon"
            >
              {t.hero.ctaContact}
            </a>
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.6 }}
        className="absolute bottom-8 left-1/2 flex -translate-x-1/2 items-center gap-3 font-mono text-[11px] uppercase tracking-[0.3em] text-muted2"
      >
        <ArrowDown className="h-4 w-4 animate-bounce text-neon" />
        {t.hero.scroll}
      </motion.div>
    </section>
  );
};

export default Hero;
